import type { FieldCatalogEntry, FieldType, OutputMode, Tool } from '../../types';
import type { MarketXtractorTool, ToolInputSchema, ToolOutputSpec } from '../types/tools';

type RawTool = Partial<MarketXtractorTool> & Record<string, any>;

const OUTPUT_MODE_SET = new Set<OutputMode>([
  'json',
  'csv',
  'pdf',
  'html',
  'text',
  'webhook',
  'email',
  'social_post',
]);

const ensureArray = (value: unknown, separators = /[|,]/): string[] => {
  if (Array.isArray(value)) {
    return value
      .flatMap((entry) => ensureArray(entry, separators))
      .filter(Boolean);
  }

  if (typeof value !== 'string') {
    return [];
  }

  return value
    .split(separators)
    .map((entry) => entry.trim())
    .filter(Boolean);
};

const normalizeFieldType = (value: unknown): FieldType => {
  switch (String(value || '').toLowerCase()) {
    case 'integer':
    case 'float':
    case 'double':
    case 'number':
      return 'number';
    case 'boolean':
      return 'boolean';
    case 'array':
      return 'array';
    case 'object':
      return 'object';
    case 'date':
    case 'datetime':
      return 'date';
    case 'url':
    case 'uri':
      return 'url';
    case 'email':
      return 'email';
    default:
      return 'string';
  }
};

const titleCaseKey = (value: string): string =>
  value
    .replace(/[_-]+/g, ' ')
    .replace(/\b\w/g, (match) => match.toUpperCase());

const parseObject = <T>(value: unknown): T | null => {
  if (!value) {
    return null;
  }

  if (typeof value === 'object') {
    return value as T;
  }

  if (typeof value !== 'string') {
    return null;
  }

  const trimmed = value.trim();
  if (!trimmed) {
    return null;
  }

  try {
    return JSON.parse(trimmed) as T;
  } catch {
    return null;
  }
};

const toSnakeToolId = (tool: RawTool): string => {
  const slug = String(tool.command_slug || '').trim();
  if (slug) {
    return slug.replace(/-/g, '_');
  }

  return String(tool.id || '').trim();
};

const toInputSchema = (inputSpec: ToolInputSchema | null): FieldCatalogEntry[] => {
  if (!inputSpec?.properties) {
    return [];
  }

  const required = new Set(inputSpec.required || []);

  return Object.entries(inputSpec.properties).map(([fieldKey, fieldConfig]) => {
    const config = (fieldConfig || {}) as Record<string, any>;

    return {
      field_key: fieldKey,
      label: config.description || titleCaseKey(fieldKey),
      type: normalizeFieldType(config.type),
      example_value: config.example ?? config.default,
      required: required.has(fieldKey),
    };
  });
};

const toSupportedOutputModes = (outputSpec: ToolOutputSpec | null): OutputMode[] => {
  const candidateModes = [
    ...(outputSpec?.downloadable_formats || []),
    ...((outputSpec?.rendering?.downloadable_formats_field && outputSpec.downloadable_formats) || []),
  ];

  return Array.from(
    new Set(
      candidateModes
        .map((value) => String(value).trim().toLowerCase())
        .filter((value): value is OutputMode => OUTPUT_MODE_SET.has(value as OutputMode))
    )
  );
};

export const normalizeTool = (rawTool: RawTool): Tool => {
  const responseModes = ensureArray(rawTool.response_modes);
  const inputSpec = parseObject<ToolInputSchema>(rawTool.input_schema_json);
  const outputSpec = parseObject<ToolOutputSpec>(rawTool.output_spec_json);
  const creditCost = Number(rawTool.credit_cost || 0);
  const tags = ensureArray(rawTool.tags);
  const badges = creditCost === 0 && !tags.includes('Free') && !tags.includes('free')
    ? [...tags, 'Free']
    : tags;

  return {
    ...rawTool,
    id: toSnakeToolId(rawTool),
    catalogId: String(rawTool.id || ''),
    commandSlug: String(rawTool.command_slug || ''),
    resultEngine: String(rawTool.result_engine || ''),
    resultTemplateId: String(rawTool.result_template_id || ''),
    resultRenderMap: parseObject<Record<string, any>>(rawTool.result_render_map_json),
    name: String(rawTool.tool_name || rawTool.name || ''),
    category: rawTool.category || '',
    subCategory: ensureArray(rawTool.subcategories),
    description: String(rawTool.short_description || rawTool.description || ''),
    longDescription: String(rawTool.long_description || rawTool.longDescription || rawTool.short_description || rawTool.description || ''),
    creditCost,
    status: String(rawTool.status || 'active'),
    badges,
    icon: String(rawTool.icon_emoji || rawTool.icon || '🧩'),
    form_structure_html: String(rawTool.form_structure_html || ''),
    responseModes,
    inputSchema: toInputSchema(inputSpec),
    inputSpec,
    outputSpec,
    supportedOutputModes: toSupportedOutputModes(outputSpec),
    creditRules: String(rawTool.credit_rules || ''),
    visibility: String(rawTool.visibility || ''),
    minPlanRequired: String(rawTool.min_plan_required || ''),
    statistics: {
      activations: Number(rawTool.enabled_count || 0),
      totalExecutions: Number(rawTool.usage_count || 0),
    },
  };
};

export const normalizeToolId = (value: string): string => value.replace(/-/g, '_');
